import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { COLUMNS, PAGINATION_CURRENT_PAGE, PAGINATION_START } from "./constants";
import { DatePicker, Input, Tooltip } from "antd";
import moment from "moment";
import { Button } from "primereact/button";
import './index.scss';
import styles from './index.module.scss';
import _ from './../../../i18n';
import lodash from 'lodash';
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { parsePreference } from "../../../utils";
import AnimatedLoadingIcon from "../../common/Icons/AnimatedLoadingIcon";
import CroissanceFilters from "../Components/CroissanceFilters/CroissanceFilters";
import { getClientsToMonitor, saveRiskyClients } from "../../../api";
import { FlagIcon, IconSend, RecoveryConvertIcon } from "../../common/Icons";
import DangerTriangleIcon from "../../common/Icons/DangerTriangleIcon";
import classNames from "classnames";
import { CalendarWithTextIcon } from "../../common/Icons/CalendarWithTextIcon";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import CollaboratorCorrectionCard from "../../CollaboratorSpace/ProductionTracking/EncodingProgression/CollaboratorCorrectionCard";
import { isMailSentToClientWithStatus, sendMailToFollowupClient } from "../services";
import SendMailToClientModal from "../Components/SendMailToClientModal/SendMailToClientModal";

const MODAL_TEXT_30_DAYS_TYPE = 1;
const MODAL_TEXT_6_MONTHS_TYPE = 2;

export default function CroissanceClientsToMonitorPage({
  changeSelectedYear,
  collaborator,
  preferences
}) {
  const { token, navCommunity, user, loggedAs } = useSelector((state) => state.auth);
  const { language } = useSelector((state) => state.params);
  const [data, setData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [correctionCardClient, setCorrectionCardClient] = useState(null);
  const [modifiedRows, setModifiedRows] = useState([]);
  const modifiedRowsRef = useRef(modifiedRows);
  const [showTextModal, setShowTextModal] = useState(false);
  const [selectedTextType, setSelectedTextType] = useState(null);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [useAdditionalEmail, setUseAdditionalEmail] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [selectedRow, setSelectedRow] = useState({
    appelRdv30Days: false,
    appelRdv6Months: false,
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [currentPage, setCurrentPage] = useState(PAGINATION_CURRENT_PAGE);
  const [pageStart, setPageStart] = useState(PAGINATION_START);
  const [isSaving, setIsSaving] = useState({
    comment: false,
    rowData: false
  });
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [filters, setFilters] = useState({
    keyWord: "",
    clientGroup: [],
    bu: parsePreference(preferences.financial).croissance.allowViewDataBy === 100 &&
      collaborator
        ? [collaborator.bu]
        : [],
    zone: parsePreference(preferences.financial).croissance.allowViewDataBy === 101 &&
      collaborator
        ? collaborator.zoneId
        : null,
    partner:
      parsePreference(preferences.financial).croissance.allowViewDataBy === 11
        ? user?.id
        : null,
    collaborator: [10, 100, 101, 110].includes(
      parsePreference(preferences.financial).croissance.allowViewDataBy
    ) && collaborator
      ? Number(collaborator.id)
      : null,
    role: null,
    pageSize: 50,
    year: new Date().getFullYear().toString(),
  });

  let getClientsToMonitorQuery = useQuery(
    ["getClientsToMonitor", token, navCommunity, currentPage, pageStart, filters],
    async () => {
      const allowViewDataBy = parsePreference(
        preferences.financial
      ).croissance.allowViewDataBy;
      if (
        [10, 110].includes(allowViewDataBy) &&
        (!filters || !filters.collaborator)
      ) {
        setIsLoadingData(false);
        setData([]);
        setDisplayedData([]);
        setTotalDataCount(0);
        return [];
      } else if (token && navCommunity) {
        setIsLoadingData(true);
        try {
          const response = await getClientsToMonitor({
            token,
            organizationId: navCommunity.id,
            start: pageStart,
            filters
          });
          let data = response.data.data.items
          const totals = response.data.data.totals;

          // add attribute to control display/hide of prestatToBeDone
          data = data.map(item => ({
            ...item,
            displayAllPrestatToBeDone: false,
            updateCompensationField: false,
            updateReasonForDepartureField: false,
          }));

          setData(data);
          setDisplayedData(data);
          setTotalDataCount(response.data.data.totalCount);
          // setTotalsData(prevState => {
          //   const newVal = {...prevState[0], ...totals};
          //   return [newVal];
          // });
        } catch (e) {
          console.error("Error while fetching old clients", {e})
          return null;
        } finally {
          setIsLoadingData(false);
        }
      }
    }
  );

  useEffect(() => {
    COLUMNS.forEach((col) => {
      if (col.field === 'clientName') {
        col.body = clientColumnBodyTemplate;
      } if (col.field === 'manager') {
        col.body = managerBodyTemplate;
      } else if (col.field === 'status') {
        col.body = statusColumnBodyTemplate;
      } else if (col.field === 'rdv') {
        col.body = rdvColumnBodyTemplate;
      } else if (col.field === 'comment') {
        col.body = commentColumnBodyTemplate;
      } else if (col.field === 'actions') {
        col.body = actionsColumnBodyTemplate;
      }
    });
  }, []);

  useEffect(() => {
    modifiedRowsRef.current = modifiedRows;
  }, [modifiedRows]);

  const clientColumnBodyTemplate = (rowData) => {
    if (rowData.id === 0) return (<span className="total-title">Total</span>);

    const clientName = rowData.client.name;
    const clientGroupDescription = rowData.client.groupDescription;
    const managerBU = rowData.manager.bu;

    return (
      <div className="client-name-cell">
        <span className="client-name">{clientName}</span>
        <div className="client-other-info">
          <div className="client-type">
            <div>{clientGroupDescription} - {managerBU}</div>
            <div>ERP ID : {rowData.client.erpId}</div>
          </div>
        </div>
      </div>
    )
  }

  const managerBodyTemplate = (rowData) => {
    if (rowData.id === 0) return '--';

    const managerName = rowData.manager.name;

    return (
      <div>
        {managerName}
      </div>
    )
  }

  const statusColumnBodyTemplate = (rowData) => {
    if (rowData.id === 0) return (<span>--</span>);

    return (
      <div className="status-cell">
        {renderStatusTags(rowData)}
      </div>
    )
  }

  const renderStatusTags = (rowData) => {
    if (rowData.id === 0) return (<span>--</span>);

    const status = rowData.status;

    return (
      <div className={classNames({
        [styles.tag]: true,
        [styles.tag_info]: status === 'RECOVERED',
        [styles.tag_warning]: status === 'RISKY',
      })}>
        {status === 'RECOVERED' && (
          <RecoveryConvertIcon color="#3F83F8" />
        )}
        {status === 'RISKY' && (
          <DangerTriangleIcon fillColor="#bb9762" size={10} />
        )}
        {_(status + '_CLIENT')}
      </div>
    );
  }

  const rdvColumnBodyTemplate = (rowData, args) => {
    if (rowData.id === 0) return (<span>--</span>);

    let date = null
    const keys = args.field.split('.');
    const hasValue = keys.reduce((obj, key) => obj && obj[key], rowData) &&
    keys.reduce((obj, key) => obj && obj[key], rowData) !== 'x';

    if (hasValue) {
      const [year, month, day] = rowData.rdv.split("-");
      date = new Date(year, month - 1, day);
    }

    return (
      <DatePicker
        value={!hasValue ? null : moment(date, "DD/MM")}
        onChange={(date) => {
          const year = date._d.getFullYear();
          const month = date._d.getMonth() + 1;
          const day = date._d.getDate();
          handleRowModification(rowData, 'rdv', `${year}-${month}-${day}`);
        }}
        format={"DD/MM"}
        picker="day"
        className="cell-datepicker-input"
      />
    )
  }

  const commentColumnBodyTemplate = (rowData, args) => {
    if (rowData.id === 0) return (<span>--</span>);

    const fieldValue = rowData[args.field];

    return (
      <div className="cell-input-field">
        <Input.TextArea
          className={`input`}
          value={fieldValue}
          name={args.field}
          autoSize={{ minRows: 1, maxRows: 6 }}
          onChange={(e) => {
            handleRowModification(rowData, args.field, e.target.value);
          }}
        />
      </div>
    );
  }

  const actionsColumnBodyTemplate = (rowData, args) => {
    if (rowData.id === 0) return (<span>--</span>);

    const thirtyDays = rowData?.thirtyDaysMeeting ? rowData.thirtyDaysMeeting : null;
    const sixMonths = rowData?.sixMonthsMeeting ? rowData.sixMonthsMeeting : null;

    return (
      <div className="clients-to-monitor-actions">
        <Tooltip title="Envoyer un email au client">
          <span
            className={classNames({
              "display-info-button": true,
              "active": isMailSentToClientWithStatus(rowData.status, rowData?.emailNotificationsSent)
            })}
            onClick={() => {
              setSelectedRow(rowData);
              setShowSendMailModal(true)
            }}
          >
            <IconSend width={17} height={17} />
          </span>
        </Tooltip>
        {(rowData.status === 'RISKY') && (
          <Tooltip title="30 jours remarques">
            <span
              className="display-info-button"
              onClick={() => handleShowTextModal(true, rowData, MODAL_TEXT_30_DAYS_TYPE)}
              style={{
                backgroundColor: thirtyDays?.isDone === 1 ? "#E3F9F5" : "#FFFFFF",
                borderColor: thirtyDays?.isDone === 1 ? "#02AF8E" : "#3C4E64"
              }}
            >
              <CalendarWithTextIcon
                size={22}
                text="J+30"
                stroke={thirtyDays?.isDone === 1 ? "#02AF8E" : "#3C4E64"}
                fill={thirtyDays?.isDone === 1 ? "#E3F9F5" : "#FFFFFF"}
              />
            </span>
          </Tooltip>
        )}

        {(rowData.status === 'RECOVERED')  && (
          <Tooltip title="6 mois remarques">
            <span
              className="display-info-button"
              onClick={() => handleShowTextModal(true, rowData, MODAL_TEXT_6_MONTHS_TYPE)}
              style={{
                backgroundColor: sixMonths?.isDone === 1 ? "#E3F9F5" : "#FFFFFF",
                borderColor: sixMonths?.isDone === 1 ? "#02AF8E" : "#3C4E64"
              }}
            >
              <CalendarWithTextIcon
                size={22}
                text="M+6"
                stroke={sixMonths?.isDone === 1 ? "#02AF8E" : "#3C4E64"}
                fill={sixMonths?.isDone === 1 ? "#E3F9F5" : "#FFFFFF"}
              />
            </span>
          </Tooltip>
        )}

        {rowData.status === 'RECOVERED' && (
          <Tooltip title="Voir le plan correctif">
            <span
              className="display-info-button"
              onClick={() => {
                setCorrectionCardClient({
                  id: rowData.client.id,
                  title: {
                    label: rowData.client.name,
                    responsables: [{
                      name: rowData.manager.name,
                      uid: rowData.manager.uid,
                    }],
                  },
                });
              }}
            >
              <FlagIcon size={17} fill="#000" strokeWidth={0.7} />
            </span>
          </Tooltip>
        )}
      </div>
    )
  }

  const renderSideBarFrame = () => {
    return (
      <div className="fadePage">
        <div className={`sidebar ${correctionCardClient && "display"}`}>
          <CollaboratorCorrectionCard
            organization={navCommunity ? navCommunity.id : null}
            token={token}
            year={filters.year}
            loggedAs={loggedAs}
            creator={user}
            onClose={() => {
              setCorrectionCardClient(null);
            }}
            client={correctionCardClient}
            lng={language}
          />
        </div>
      </div>
    );
  };

  const handleRowModification = (rowData, modifiedPropertyName, modifiedPropertyValue) => {
    const keys = modifiedPropertyName.split('.');
    const updatedRowData = {
      ...rowData,
      [modifiedPropertyName]: modifiedPropertyValue
    };

    // Set the new value in the cloned rowData
    setValue({ ...rowData }, keys, modifiedPropertyValue);

    // Update the modifiedRows state

    setModifiedRows(prevState => {
      // Check if the row already exists in modifiedRows
      const modifiedRowIndex = prevState.findIndex(i => i.id === rowData.id);

      if (modifiedRowIndex > -1) {
        // Update existing modified row
        const updatedModifiedRows = [...prevState];
        updatedModifiedRows[modifiedRowIndex] = { ...updatedModifiedRows[modifiedRowIndex], ...updatedRowData };
        return updatedModifiedRows;
      } else {
        // Add new modified row
        return [...prevState, updatedRowData];
      }
    });

    // Update the displayedData state
    setDisplayedData(prevState =>
      prevState.map(i =>
        i.id === rowData.id ? { ...rowData, [modifiedPropertyName]: modifiedPropertyValue } : i
      )
    );
  }

  const setValue = (obj, keys, value) => {
    keys.reduce((accumulator, key, index) => {
      // If we're at the last key, set the value
      if (index === keys.length - 1) {
        accumulator[key] = value;
      } else {
        // If the key doesn't exist, create an empty object
        if (!accumulator[key]) {
          accumulator[key] = {};
        }
        return accumulator[key]; // Move to the next nested object
      }
      return accumulator;
    }, obj);
  };

  const handleSaveModifiedRows = () => {
    setIsSaving(prevState => ({...prevState, rowData: true}));

    saveRiskyClients({token, organizationId: navCommunity.id, items: modifiedRows}).then((res) => {
      if (res.status === 200) {
        setModifiedRows([]);
        toast.success("Les données ont été enregistrées avec succès");
        getClientsToMonitorQuery.refetch();
      } else {
        toast.error("Une erreur est survenue lors de l'enregistrement des données");
      }
    }).catch(e => {
      toast.error("Une erreur est survenue lors de l'enregistrement des données");
      console.error("Error saving data : ", e);
    }).finally(() => {
      setIsSaving(prevState => ({...prevState, rowData: false}));
    });
  }

  const handleSaveComment = () => {
    let {
      id,
      clientId,
      comments,
      thirtyDaysMeeting,
      sixMonthsMeeting
    } = selectedRow;

    if (thirtyDaysMeeting?.isDone) {
      thirtyDaysMeeting = {
        ...thirtyDaysMeeting,
        isDone: thirtyDaysMeeting.isDone ? 1 : 0
      };
    }

    if (sixMonthsMeeting?.isDone) {
      sixMonthsMeeting = {
        ...sixMonthsMeeting,
        isDone: sixMonthsMeeting.isDone ? 1 : 0
      };
    }

    setIsSaving(prevState => ({...prevState, comment: true}));
    saveRiskyClients({
      token,
      organizationId: navCommunity.id,
      items: [{
        id,
        clientId,
        comments,
        thirtyDaysMeeting,
        sixMonthsMeeting
      }]
    }).then((res) => {
      if (res.status === 200) {
        setSelectedRow(null);
        toast.success("Commentaire enregistré avec succès.");
        getClientsToMonitorQuery.refetch();
      } else {
        toast.error("Échec de l'enregistrement du commentaire. Veuillez réessayer.");
      }
    }).catch(e => {
      toast.error("Échec de l'enregistrement du commentaire. Veuillez réessayer.");
      console.error("Error saving comment : ", e);
    }).finally(() => {
      setIsSaving(prevState => ({...prevState, comment: false}));
    });
  }

  useEffect(() => {
    setCurrentPage(0);
    setPageStart(0);
  }, [filters.pageSize]);

  useEffect(() => {
    changeSelectedYear(filters.year);
  }, [filters.year]);

  const handleShowTextModal = (isVisible, rowData, type = MODAL_TEXT_30_DAYS_TYPE) => {
    if (rowData) {
      setSelectedRow(rowData);
    } else {
      setSelectedRow(null);
    }
    setSelectedTextType(type);
    setShowTextModal(isVisible);
  }

  return (
    <div className="clients-in-risk-container">
       <div className="datatable-header">
        <div className="datatable-header-row">
          <div className="filters" style={{display: "flex", gap: "8px"}}>
            <CroissanceFilters
              filterFormIsVisible={false}
              filter={filters}
              nbResult={totalDataCount}
              updateFilter={setFilters}
              fetching={getClientsToMonitorQuery.isFetching}
              collaborator={collaborator}
              preferences={preferences}
            />
          </div>
        </div>
        <div className="datatable-header-row">
          <p className="data-count">
            Nombre de dossiers: <span>{totalDataCount}</span>
          </p>
        </div>
      </div>
      <DataTable
        value={displayedData}
        lazy
        paginator
        totalRecords={totalDataCount}
        first={currentPage * filters.pageSize + 1}
        rows={filters.pageSize}
        onPage={(e) => {
          setCurrentPage(e.page);
          setPageStart(e.page * filters.pageSize);
        }}
        tableClassName="clients-in-risk-datatable"
        loading={isLoadingData}
        loadingIcon={
          <div className="ripple_loading" style={{alignItems: 'center'}}>
            <img src={"/img/Ripple-1s-150px.svg"} alt="" width={80} height={80} />
          </div>
        }
      >
        {
          COLUMNS.map(col => (
            <Column
              className="datatable-column"
              key={col.field}
              field={col.field}
              header={col.header}
              style={{width: col.style?.width ?? 'auto'}}
              sortable={col.sortable ?? false}
              body={col.body ?? null}
              alignHeader={col?.alignHeader ?? 'left'}
              resizeable={col?.resizeable ?? false}
              align={col?.align ?? 'center'}
            />
          ))
        }
      </DataTable>

      {/* save/cancel buttons */}
      {modifiedRows.length > 0 && (
        <div className="action-buttons">
          <Button
            label="Annuler"
            severity="secondary"
            size="small"
            onClick={() => {
              setModifiedRows([]);
              setDisplayedData(lodash.cloneDeep(data));
            }}
            disabled={isSaving.rowData}
          />
          <Button
            size="small"
            label={
              isSaving.rowData ? (
                <AnimatedLoadingIcon color="#ffffff" />
              ) : 'Enregistrer'
            }
            disabled={isSaving.rowData}
            severity="success"
            onClick={handleSaveModifiedRows}
          />
        </div>
      )}

      {/* comments modal dialog */}
      {showTextModal && (
        <Dialog
          visible={showTextModal && selectedRow}
          onHide={() => handleShowTextModal(false)}
          className="text-modal-dialog"
          header={
            selectedTextType === MODAL_TEXT_30_DAYS_TYPE ? "30 jours remarques" :
            selectedTextType === MODAL_TEXT_6_MONTHS_TYPE ? "6 mois remarques" :
            "30 jours remarques"
          }
          footer={
            <div>
              <Button
                size="small"
                severity="success"
                onClick={handleSaveComment}
                disabled={isSaving.comment}
              >
                {
                  isSaving.comment ? (
                    <AnimatedLoadingIcon color="#ffffff" />
                  ):
                  'Enregistrer'
                }
              </Button>
            </div>
          }
        >
          {
            (selectedTextType === MODAL_TEXT_30_DAYS_TYPE || selectedTextType === MODAL_TEXT_6_MONTHS_TYPE) && (
              <div className="modal-check-stars">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedTextType === MODAL_TEXT_30_DAYS_TYPE
                        ? selectedRow?.thirtyDaysMeeting?.isDone ?? false
                        : selectedRow?.sixMonthsMeeting?.isDone ?? false
                    }
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (selectedTextType === MODAL_TEXT_30_DAYS_TYPE) {
                        setSelectedRow(prevState => ({
                          ...prevState,
                          thirtyDaysMeeting: {
                            ...prevState.thirtyDaysMeeting,
                            isDone: isChecked ? 1 : 0
                          }
                        }));
                      } else if (selectedTextType === MODAL_TEXT_6_MONTHS_TYPE) {
                        setSelectedRow(prevState => ({
                          ...prevState,
                          sixMonthsMeeting: {
                            ...prevState.sixMonthsMeeting,
                            isDone: isChecked ? 1 : 0
                          }
                        }));
                      }
                    }}
                  />
                  Appel / Rendez-vous
                </label>

                <div className="star-rating">
                  <span className="rating-label">Insatisfait</span>
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <img
                        key={star}
                        src={
                          selectedTextType === MODAL_TEXT_30_DAYS_TYPE ?
                          star <= (selectedRow?.thirtyDaysMeeting?.rating || 0)
                          ? "/img/icon/star.svg"
                          : "/img/icon/light_star.svg" :
                          star <= (selectedRow?.sixMonthsMeeting?.rating || 0)
                          ? "/img/icon/star.svg"
                          : "/img/icon/light_star.svg"
                        }
                        alt={`Star ${star}`}
                        onClick={() => {
                          if (selectedTextType === MODAL_TEXT_30_DAYS_TYPE) {
                            setSelectedRow(prevState => ({
                              ...prevState,
                              thirtyDaysMeeting: {
                                ...prevState.thirtyDaysMeeting,
                                rating: star
                              }
                            }));
                          } else if (selectedTextType === MODAL_TEXT_6_MONTHS_TYPE) {
                            setSelectedRow(prevState => ({
                              ...prevState,
                              sixMonthsMeeting: {
                                ...prevState.sixMonthsMeeting,
                                rating: star
                              }
                            }));
                          }
                        }}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    ))}
                  </div>
                  <span className="rating-label">Ravi</span>
                </div>
              </div>
            )
          }
          <span className="p-float-label" style={{marginTop: '20px'}}>
            <InputTextarea
              id="comments"
              className="textarea"
              rows={5}
              value={
                selectedTextType === MODAL_TEXT_30_DAYS_TYPE ? selectedRow?.thirtyDaysMeeting?.note :
                selectedTextType === MODAL_TEXT_6_MONTHS_TYPE ? selectedRow?.sixMonthsMeeting?.note :
                selectedRow.comments
              }
              disabled={isSaving.comment}
              onChange={(e) => {
                const value = e.target.value;
                if (selectedTextType === MODAL_TEXT_30_DAYS_TYPE) {
                  setSelectedRow(prevState => ({
                    ...prevState,
                    thirtyDaysMeeting: {
                      ...prevState.thirtyDaysMeeting,
                      note: value
                    }
                  }));
                } else if (selectedTextType === MODAL_TEXT_6_MONTHS_TYPE) {
                  setSelectedRow(prevState => ({
                    ...prevState,
                    sixMonthsMeeting: {
                      ...prevState.sixMonthsMeeting,
                      note: value
                    }
                  }));
                }
              }}
            />
            <label htmlFor="comments">{
              selectedTextType === MODAL_TEXT_30_DAYS_TYPE ? "30 jours remarques" :
              selectedTextType === MODAL_TEXT_6_MONTHS_TYPE ? "6 mois remarques" :
              "30 jours remarques"
            }</label>
          </span>
        </Dialog>
      )}

      {showSendMailModal && (
        <SendMailToClientModal
          isOpen={showSendMailModal}
          client={(function getClientFields() {
            if (!selectedRow || !selectedRow?.client) return null;

            const { id, name, email } = selectedRow.client;
            const { emailNotificationsSent, status } = selectedRow;

            return {
              id,
              name,
              email,
              status,
              emailNotificationsSent
            };
          })()}
          additionalEmail={additionalEmail}
          setAdditionalEmail={setAdditionalEmail}
          useAdditionalEmail={useAdditionalEmail}
          setUseAdditionalEmail={setUseAdditionalEmail}
          isSending={isSendingMail}
          onClose={() => {
            setShowSendMailModal(false);
            setSelectedRow(null);
            setAdditionalEmail('');
            setUseAdditionalEmail(false);
          }}
          onConfirm={() => {
            setIsSendingMail(true);

            const client = {
              name: selectedRow.client.name,
              email: additionalEmail ? additionalEmail : selectedRow?.client?.email
            }

            sendMailToFollowupClient({
              token,
              language,
              user,
              client,
              meetingDate: "10/06/2025",
              partner: selectedRow.manager,
              organization: navCommunity,
              clientStatus: selectedRow.status
            }).then((response) => {
              if (response.data.statusCode === 200) {
                const modifiedItem = { ...selectedRow, isMailSentToClient: 1, emailSentTo: client.email };

                saveRiskyClients({
                  token,
                  organizationId: navCommunity.id,
                  items: [modifiedItem]
                }).then((res) => {
                  if (res.status === 200) {
                    toast.success("L'email a été envoyé au client avec succès.");
                    setShowSendMailModal(false);
                    setSelectedRow(null);
                    setAdditionalEmail('');
                    setUseAdditionalEmail(false);
                    getClientsToMonitorQuery.refetch();
                  } else {
                    toast.success("Échec de l'envoi de l'email au client.");
                  }
                })
              } else {
                toast.success("Échec de l'envoi de l'email au client.");
              }
            }).finally(() => {
              setIsSendingMail(false);
            });
          }}
        />
      )}

      {correctionCardClient && renderSideBarFrame()}
    </div>
  )
}
